import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * Optimized Earth Globe Component
 *
 * This component replaces the heavy 2.3MB earth-tp.png with optimized versions:
 * - WebP format (56KB) for modern browsers
 * - Optimized PNG (852KB) for older browsers
 * - Tiny placeholder (2KB) for instant loading
 *
 * Usage: Replace the earth globe div in Hero.tsx (around line 160-178) with this component
 */
export const OptimizedEarthGlobe: React.FC<{
    className?: string;
    style?: React.CSSProperties;
}> = ({ className = '', style = {} }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [supportsWebP, setSupportsWebP] = useState(false);

    useEffect(() => {
        // Check WebP support
        const webP = new Image();
        webP.onload = webP.onerror = function () {
            setSupportsWebP(webP.height === 2);
        };
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';

        // Preload the appropriate image
        const img = new Image();
        img.onload = () => setImageLoaded(true);
        img.src = supportsWebP
            ? '/globe_section/earth-tp.webp'
            : '/globe_section/earth-tp-optimized.png';
    }, []);

    const getImageUrl = () => {
        if (!imageLoaded) {
            // Use placeholder while loading
            return '/globe_section/earth-tp-placeholder.jpg';
        }
        return supportsWebP
            ? '/globe_section/earth-tp.webp'
            : '/globe_section/earth-tp-optimized.png';
    };

    return (
        <motion.div
            className={`absolute ${className}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{
                opacity: imageLoaded ? 0.5 : 0.3,
                scale: imageLoaded ? 1 : 0.95
            }}
            transition={{
                duration: 1.2,
                ease: "easeOut"
            }}
            style={{
                width: '680px',
                height: '680px',
                left: 'calc(50% - 720px/2)',
                top: 'calc(50% - 800px/2)',
                backgroundImage: `url("${getImageUrl()}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                // Use luminosity to make black transparent
                maskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)',
                filter: imageLoaded
                    ? 'brightness(2.1) contrast(0.9)'
                    : 'brightness(2.1) contrast(0.9) blur(8px)',
                mixBlendMode: 'soft-light',
                zIndex: 1,
                transition: 'filter 0.6s ease-out',
                ...style
            }}
        />
    );
};

/**
 * Alternative: Picture Element Implementation
 * Use this if you prefer a more semantic HTML5 approach
 */
export const EarthGlobePicture: React.FC = () => {
    return (
        <div className="absolute"
            style={{
                width: '680px',
                height: '680px',
                left: 'calc(50% - 720px/2)',
                top: 'calc(50% - 800px/2)',
                zIndex: 1
            }}
        >
            <picture>
                {/* WebP for modern browsers */}
                <source
                    srcSet="/globe_section/earth-tp.webp"
                    type="image/webp"
                />
                {/* Optimized PNG fallback */}
                <img
                    src="/globe_section/earth-tp-optimized.png"
                    alt=""
                    loading="eager" // Above fold, load immediately
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        maskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)',
                        opacity: 0.5,
                        filter: 'brightness(2.1) contrast(0.9)',
                        mixBlendMode: 'soft-light'
                    }}
                />
            </picture>
        </div>
    );
};

export default OptimizedEarthGlobe;